(() => {
  const drawers = document.querySelectorAll("[data-hn-page-drawer]");
  let returnFocus = null;
  let highlightTimer = 0;

  const i18nSource = document.querySelector("[data-hn-product-help-i18n]");
  const i18n = i18nSource?.dataset || {};
  const translated = (key, fallback) => i18n[key] || fallback;

  const slugify = (value) =>
    value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

  const setActiveDrawerCategory = (drawer, targetId) => {
    drawer.querySelectorAll("[data-hn-drawer-jump]").forEach((button) => {
      button.classList.toggle("is-active", button.dataset.hnDrawerJump === targetId);
    });
  };

  const enhanceFaqAccordions = (drawer, sections) => {
    if (drawer.dataset.hnPageDrawer !== "faq") return;

    sections.forEach((section) => {
      let currentBody = null;

      [...section.children].forEach((node) => {
        if (node.tagName === "H3") {
          const details = document.createElement("details");
          details.className = "hn-page-drawer__faq-item";
          if (node.id) details.id = node.id;
          const summary = document.createElement("summary");
          summary.textContent = node.textContent;

          currentBody = document.createElement("div");
          currentBody.className = "hn-page-drawer__faq-answer";

          details.append(summary, currentBody);
          section.insertBefore(details, node);
          node.remove();
        } else if (currentBody && node.tagName !== "H2") {
          currentBody.appendChild(node);
        }
      });
    });
  };

  const enhancePolicyDrawer = (drawer) => {
    if (!drawer || drawer.dataset.hnPolicyEnhanced === "true") return;
    const content = drawer.querySelector("[data-hn-policy-drawer-content]");
    const navigation = drawer.querySelector("[data-hn-page-drawer-nav]");
    if (!content || !navigation) return;

    drawer.dataset.hnPolicyEnhanced = "true";
    let currentSection = null;
    let sectionIndex = 0;

    [...content.children].forEach((node) => {
      if (node.tagName === "H2") {
        sectionIndex += 1;
        currentSection = document.createElement("section");
        currentSection.className = "hn-page-drawer__policy-section";
        content.insertBefore(currentSection, node);
        currentSection.appendChild(node);

        const drawerName = drawer.dataset.hnPageDrawer || "page";
        const id = node.id || `drawer-${drawerName}-${slugify(node.textContent) || sectionIndex}`;
        node.id = id;

        const button = document.createElement("button");
        button.type = "button";
        button.dataset.hnDrawerJump = id;
        button.textContent = node.textContent;
        if (sectionIndex === 1) button.classList.add("is-active");
        navigation.appendChild(button);
      } else if (currentSection) {
        currentSection.appendChild(node);
      } else if (node.tagName === "P") {
        node.classList.add("hn-page-drawer__policy-lead");
      }
    });

    const shell = drawer.querySelector(".hn-page-drawer__shell");
    const sections = [...content.querySelectorAll(".hn-page-drawer__policy-section")];
    enhanceFaqAccordions(drawer, sections);
    if (!shell || !sections.length || !("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        setActiveDrawerCategory(drawer, visible.target.querySelector("h2").id);
      },
      { root: shell, rootMargin: "-138px 0px -58% 0px", threshold: [0, 0.2, 0.55] }
    );

    sections.forEach((item) => observer.observe(item));
  };

  const findFaqTarget = (drawer, targetName) => {
    const idTarget = drawer.querySelector(
      `#faq-${CSS.escape(targetName)}`
    );

    if (idTarget) return idTarget;

    const matchText = {
      material: [
        "informasjon om materiale",
        "led-neon",
        "material",
        "led neon",
        "glass neon",
        "led- oder glasneon",
        "glasneon",
      ],
      size: [
        "størrelse og mål",
        "lengste side",
        "size",
        "longest side",
        "gr\u00f6\u00dfe",
        "groesse",
        "l\u00e4ngste seite",
        "laengste seite",
      ],
      color: ["farge", "hvilke farger kan jeg velge", "color", "colour"],
      backing: [
        "støtte",
        "akryl",
        "backing options",
        "backing",
        "acrylic",
        "r\u00fcckwandoptionen",
        "r\u00fcckwand",
        "r\u00fcckhalt",
        "acryl",
      ],
      dimmable: [
        "kan dimmes",
        "adjust the brightness",
        "dimmable",
        "brightness",
        "dimmer",
        "helligkeit einstellen",
        "dimmbar",
        "helligkeit",
      ],
      "backing-shape": [
        "bakside kuttet til shape",
        "backing cut to shape",
        "cut to shape",
        "cut-to-shape",
        "backing shape",
        "r\u00fcckwand in form geschnitten",
        "in form schneiden",
        "konturschnitt",
      ],
      outdoor: [
        "utendørs bruk",
        "used outdoors",
        "outdoor",
        "water-resistant",
        "im au\u00dfenbereich",
        "drau\u00dfen",
        "draussen",
        "wasserbest\u00e4ndig",
      ],
    }[targetName] || [targetName];

    return [
      ...drawer.querySelectorAll(
        "h2, h3, h4, summary"
      ),
    ].find((heading) => {
      const text =
        heading.textContent.toLowerCase();

      return matchText.some((term) =>
        text.includes(term)
      );
    });
  };

  const getFaqDrawerTitle = (drawer) => {
    const explicitTitle = drawer.querySelector(
      [
        "[data-hn-page-drawer-title]",
        "[data-hn-page-drawer-heading]",
        ".hn-page-drawer__title",
        ".hn-page-drawer__heading",
      ].join(",")
    );

    if (explicitTitle) return explicitTitle;

    const content = drawer.querySelector(
      "[data-hn-policy-drawer-content]"
    );

    return (
      [...drawer.querySelectorAll("h1, h2")].find(
        (heading) =>
          !content?.contains(heading) &&
          !heading.closest(
            "[data-hn-page-drawer-nav]"
          )
      ) || null
    );
  };

  const setFaqDrawerTitle = (
    drawer,
    targetTitle = ""
  ) => {
    const title = getFaqDrawerTitle(drawer);

    if (!title) return;

    if (!drawer.dataset.hnFaqOriginalTitle) {
      drawer.dataset.hnFaqOriginalTitle =
        title.textContent.trim();
    }

    title.textContent = targetTitle
      ? targetTitle
      : drawer.dataset.hnFaqOriginalTitle ||
        translated("hnI18nProductFaqs", "Vanlige spørsmål om neonskilt");
  };

  const getFaqTargetTitle = (target) => {
    if (!target) return "";

    const heading = target.matches("summary")
      ? target
      : target.querySelector(":scope > summary") || target;

    return heading.textContent.trim();
  };

  const hideForFaqFilter = (element) => {
    if (
      !element ||
      element.dataset.hnFaqFiltered === "true"
    ) {
      return;
    }

    element.dataset.hnFaqFiltered = "true";

    element.dataset.hnFaqPreviousDisplay =
      element.style.getPropertyValue("display");

    element.dataset.hnFaqPreviousDisplayPriority =
      element.style.getPropertyPriority("display");

    element.hidden = true;

    element.style.setProperty(
      "display",
      "none",
      "important"
    );
  };

  const ensureFaqViewAllLink = (drawer) => {
    const content = drawer.querySelector(
      "[data-hn-policy-drawer-content]"
    );

    if (!content) return null;

    let wrapper = content.querySelector(
      "[data-hn-view-all-faq-wrap]"
    );

    if (wrapper) return wrapper;

    wrapper = document.createElement("div");
    wrapper.dataset.hnViewAllFaqWrap = "true";
    wrapper.hidden = true;
    wrapper.style.display = "none";
    wrapper.style.justifyContent = "center";
    wrapper.style.padding = "30px 0 10px";
    wrapper.style.marginTop = "28px";
    wrapper.style.borderTop =
      "1px solid rgba(0, 0, 0, 0.12)";

    const button =
      document.createElement("button");

    button.type = "button";
    button.dataset.hnViewAllFaq = "true";
    button.textContent =
      translated("hnI18nViewAllFaqs", "Se alle vanlige spørsmål");

    button.style.border =
      "1px solid rgba(158, 0, 255, 0.35)";
    button.style.borderRadius = "999px";
    button.style.padding = "11px 20px";
    button.style.background =
      "rgba(158, 0, 255, 0.08)";
    button.style.color = "#8F00E8";
    button.style.fontSize = "14px";
    button.style.fontWeight = "700";
    button.style.lineHeight = "1.4";
    button.style.cursor = "pointer";

    wrapper.appendChild(button);
    content.appendChild(wrapper);

    return wrapper;
  };

  const resetFaqView = (drawer) => {
    drawer.classList.remove(
      "hn-faq-single-topic"
    );

    drawer
      .querySelectorAll(
        '[data-hn-faq-filtered="true"]'
      )
      .forEach((element) => {
        const previousDisplay =
          element.dataset
            .hnFaqPreviousDisplay || "";

        const previousPriority =
          element.dataset
            .hnFaqPreviousDisplayPriority || "";

        element.hidden = false;

        if (previousDisplay) {
          element.style.setProperty(
            "display",
            previousDisplay,
            previousPriority
          );
        } else {
          element.style.removeProperty(
            "display"
          );
        }

        element.removeAttribute(
          "data-hn-faq-filtered"
        );
        element.removeAttribute(
          "data-hn-faq-previous-display"
        );
        element.removeAttribute(
          "data-hn-faq-previous-display-priority"
        );
      });

    const viewAllWrapper =
      drawer.querySelector(
        "[data-hn-view-all-faq-wrap]"
      );

    if (viewAllWrapper) {
      viewAllWrapper.hidden = true;
      viewAllWrapper.style.display = "none";
    }
  };

  const showFaqTargetOnly = (
    drawer,
    target,
    targetName
  ) => {
    const activeSection = target.closest(
      ".hn-page-drawer__policy-section"
    );

    if (!activeSection) return;

    drawer.classList.add(
      "hn-faq-single-topic"
    );

    const navigation = drawer.querySelector(
      "[data-hn-page-drawer-nav]"
    );

    if (navigation) {
      hideForFaqFilter(navigation);
    }

    drawer
      .querySelectorAll(
        ".hn-page-drawer__policy-lead"
      )
      .forEach((element) => {
        hideForFaqFilter(element);
      });

    drawer
      .querySelectorAll(
        ".hn-page-drawer__policy-section"
      )
      .forEach((section) => {
        if (section !== activeSection) {
          hideForFaqFilter(section);
        }
      });

    const sectionHeading =
      activeSection.querySelector(":scope > h2");

    if (sectionHeading) {
      hideForFaqFilter(sectionHeading);
    }

    const individualTargets = new Set([
      "dimmable",
      "backing-shape",
      "outdoor",
    ]);

    if (individualTargets.has(targetName)) {
      const activeItem = target.matches(
        ".hn-page-drawer__faq-item"
      )
        ? target
        : target.closest(
            ".hn-page-drawer__faq-item"
          );

      if (activeItem) {
        [...activeSection.children].forEach(
          (child) => {
            if (child !== activeItem) {
              hideForFaqFilter(child);
            }
          }
        );

        activeItem.hidden = false;
        activeItem.open = true;
      }
    }

    const viewAllWrapper =
      ensureFaqViewAllLink(drawer);

    if (viewAllWrapper) {
      viewAllWrapper.hidden = false;
      viewAllWrapper.style.display = "flex";
    }
  };

  const openDrawer = (name, targetName, trigger) => {
    const drawer = document.querySelector(
      `[data-hn-page-drawer="${CSS.escape(name)}"]`
    );

    if (!drawer) return;

    enhancePolicyDrawer(drawer);
    returnFocus = trigger;

    if (name === "faq") {
      resetFaqView(drawer);
      setFaqDrawerTitle(drawer, "");

      if (targetName === "backing") {
        const ledBackingTab = drawer.querySelector(
          "#faq-backing-view-led"
        );

        if (ledBackingTab) {
          ledBackingTab.checked = true;
        }
      }
    }

    drawer.showModal();

    if (!targetName) {
      window.requestAnimationFrame(() => {
        drawer
          .querySelector(".hn-page-drawer__shell")
          ?.scrollTo({ top: 0 });
      });

      return;
    }

    window.requestAnimationFrame(() => {
      const target = findFaqTarget(
        drawer,
        targetName
      );

      if (!target) return;

      const activeSection = target.closest(
        ".hn-page-drawer__policy-section"
      );

      const categoryHeading =
        activeSection?.querySelector("h2");

      if (categoryHeading) {
        setActiveDrawerCategory(
          drawer,
          categoryHeading.id
        );
      }

      if (name === "faq") {
        setFaqDrawerTitle(
          drawer,
          getFaqTargetTitle(target)
        );

        showFaqTargetOnly(
          drawer,
          target,
          targetName
        );
      }

      target.classList.add("is-hn-target");

      drawer
        .querySelector(".hn-page-drawer__shell")
        ?.scrollTo({ top: 0 });

      window.setTimeout(() => {
        target.classList.remove("is-hn-target");
      }, 1700);
    });
  };

  const enhanceThirdPartyOptions = () => {
    const bcpo = document.querySelector("#bcpo");

    if (!bcpo) return;

    const productForm = bcpo.closest("form");
    const productBuybox = bcpo.closest(".hn-product-buybox");
    const quantitySelector = productBuybox?.querySelector(
      "block-quantity-selector"
    );
    const addToCart = productForm?.querySelector(".add-to-cart");

    if (
      productForm &&
      quantitySelector &&
      addToCart &&
      quantitySelector.parentElement !== productForm
    ) {
      productForm.insertBefore(
        quantitySelector,
        addToCart
      );
    }

    const optionTargets = {
      "størrelse": "size",
      "lengste side": "size",
      "støtte": "backing",
      farge: "color",
      color: "color",
      colour: "color",
      material: "material",
      size: "size",
      backing: "backing",
      "gr\u00f6\u00dfe": "size",
      groesse: "size",
      "r\u00fcckwand": "backing",
      "r\u00fcckhalt": "backing",
    };

    bcpo
      .querySelectorAll(
        [
          '[data-hn-option-help="features"]',
          '[data-hn-option-help="extra-features"]',
        ].join(",")
      )
      .forEach((button) => {
        button.remove();
      });

    bcpo
      .querySelectorAll(".bcpo-label")
      .forEach((label) => {
        const title = label.querySelector(".bcpo-title");

        if (!title) return;

        const normalizedTitle = title.textContent
          .toLowerCase()
          .replace(/:$/, "")
          .trim();

        if (
          normalizedTitle.includes("ekstra funksjoner") ||
          normalizedTitle.includes("extra features") ||
          normalizedTitle.includes("zusatzfunktionen")
        ) {
          label
            .querySelectorAll("[data-hn-option-help]")
            .forEach((button) => {
              button.remove();
            });

          return;
        }

        if (
          label.querySelector("[data-hn-option-help]")
        ) {
          return;
        }

        const targetName = Object.entries(
          optionTargets
        ).find(([name]) =>
          normalizedTitle.includes(name)
        )?.[1];

        if (!targetName) return;

        const button =
          document.createElement("button");

        button.className = "hn-option-help";
        button.type = "button";
        button.dataset.hnOptionHelp = targetName;

        button.setAttribute(
          "aria-label",
          `${translated("hnI18nOpenOptionGuide", "Mer informasjon om dette valget")}: ${normalizedTitle}`
        );

        button.textContent = "i";

        title.insertAdjacentElement(
          "afterend",
          button
        );
      });

    const normalizeOptionText = (value) =>
      String(value || "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

    const featureTargets = [
      {
        labels: [
          "dimmable",
          "kan dimmes",
          "dimmbar",
        ],
        target: "dimmable",
        ariaLabel: translated("hnI18nOpenOptionGuide", "Mer informasjon om dette valget"),
      },
      {
        labels: [
          "backing cut to shape",
          "bakside kuttet til shape",
          "cut to shape",
          "backing cut-to-shape",
          "in form schneiden",
          "r\u00fcckwand in form schneiden",
          "konturschnitt",
        ],
        target: "backing-shape",
        ariaLabel: translated("hnI18nOpenOptionGuide", "Mer informasjon om dette valget"),
      },
      {
        labels: [
          "outdoor",
          "utendørs bruk",
          "outdoor only for led",
          "outdoor (only for led)",
          "draussen",
          "drau\u00dfen",
          "draussen (nur led)",
          "drau\u00dfen (nur led)",
          "draussen (nur f\u00fcr led)",
          "drau\u00dfen (nur f\u00fcr led)",
          "au\u00dfenbereich",
        ],
        target: "outdoor",
        ariaLabel: translated("hnI18nOpenOptionGuide", "Mer informasjon om dette valget"),
      },
    ];

      featureTargets.forEach((feature) => {
      if (
        bcpo.querySelector(
          `[data-hn-option-help="${CSS.escape(
            feature.target
          )}"]`
        )
      ) {
        return;
      }

      const candidates = [
        ...bcpo.querySelectorAll(
          "label, span, p, div"
        ),
      ]
        .filter((element) => {
          const text = normalizeOptionText(
            element.textContent
          );

          return feature.labels.some(
            (label) =>
              text === normalizeOptionText(label)
          );
        })
        .sort((first, second) => {
          return (
            first.children.length -
              second.children.length ||
            first.textContent.length -
              second.textContent.length
          );
        });

      const textElement = candidates[0];

      if (!textElement) return;

      const button =
        document.createElement("button");

      button.className =
        "hn-option-help hn-option-help--inline";

      button.type = "button";
      button.dataset.hnOptionHelp =
        feature.target;

      button.setAttribute(
        "aria-label",
        feature.ariaLabel
      );

      button.textContent = "i";
      button.style.marginInlineStart = "5px";
      button.style.verticalAlign = "middle";
      button.style.display = "inline-flex";
      button.style.alignItems = "center";
      button.style.justifyContent = "center";
      button.style.flex = "0 0 auto";
      button.style.transform =
        "translateY(1px)";

      if (textElement.matches("label")) {
        textElement.appendChild(button);
      } else {
        textElement.insertAdjacentElement(
          "afterend",
          button
        );
      }
    });
  };
  document.addEventListener("click", (event) => {
    const optionTrigger = event.target.closest("[data-hn-option-help]");
    if (optionTrigger) {
      event.preventDefault();
      event.stopPropagation();
      openDrawer("faq", optionTrigger.dataset.hnOptionHelp, optionTrigger);
      return;
    }
    const viewAllFaqButton = event.target.closest(
      "[data-hn-view-all-faq]"
    );

    if (viewAllFaqButton) {
      event.preventDefault();

      const drawer = viewAllFaqButton.closest(
        '[data-hn-page-drawer="faq"]'
      );

      if (!drawer) return;

      resetFaqView(drawer);
      setFaqDrawerTitle(drawer, "");

      const firstHeading = drawer.querySelector(
        ".hn-page-drawer__policy-section h2"
      );

      if (firstHeading) {
        setActiveDrawerCategory(
          drawer,
          firstHeading.id
        );
      }

      drawer
        .querySelector(".hn-page-drawer__shell")
        ?.scrollTo({
          top: 0,
          behavior: "smooth",
        });

      return;
    }
    const pageTrigger = event.target.closest("[data-hn-page-drawer-open]");
    if (pageTrigger) {
      openDrawer(pageTrigger.dataset.hnPageDrawerOpen, "", pageTrigger);
      return;
    }

    const drawerJump = event.target.closest("[data-hn-drawer-jump]");
    if (drawerJump) {
      const drawer = drawerJump.closest("[data-hn-page-drawer]");
      const target = drawer?.querySelector(`#${CSS.escape(drawerJump.dataset.hnDrawerJump)}`)?.closest(
        ".hn-page-drawer__policy-section"
      );
      if (!drawer || !target) return;

      setActiveDrawerCategory(drawer, drawerJump.dataset.hnDrawerJump);
      target.classList.add("is-target");
      target.scrollIntoView({ behavior: "smooth", block: "start" });

      window.clearTimeout(highlightTimer);
      highlightTimer = window.setTimeout(() => target.classList.remove("is-target"), 1700);
      return;
    }

    const closeButton = event.target.closest("[data-hn-page-drawer-close]");
    if (closeButton) {
      closeButton.closest("dialog")?.close();
    }
  });

  document.addEventListener("change", (event) => {
    const optionInput = event.target.closest(".variant-button-wrap input[type='radio']");
    if (optionInput) {
      const currentValue = optionInput
        .closest(".variant-button-wrap")
        ?.querySelector("[data-hn-option-current]");
      if (currentValue) currentValue.textContent = optionInput.value;
      return;
    }

    const appOptionInput = event.target.closest("#bcpo .bcpo-front-button-option");
    if (!appOptionInput) return;

    const appCurrentValue = appOptionInput
      .closest(".selector-wrapper")
      ?.querySelector(".bcpo-value");
    if (appCurrentValue) appCurrentValue.textContent = appOptionInput.value;
  });

  drawers.forEach((drawer) => {
    enhancePolicyDrawer(drawer);
    drawer.addEventListener("click", (event) => {
      if (event.target === drawer) drawer.close();
    });
    drawer.addEventListener("close", () => {
      returnFocus?.focus();
      returnFocus = null;
    });
  });

  enhanceThirdPartyOptions();
  const productBuybox = document.querySelector(".hn-product-buybox");
  if (productBuybox && "MutationObserver" in window) {
    const optionsObserver = new MutationObserver(enhanceThirdPartyOptions);
    optionsObserver.observe(productBuybox, { childList: true, subtree: true });
  }
})();
